import { useQueryClient } from "@tanstack/react-query";
import useProfile from "./useProfile";
import { useRef, useState } from "react";
import { useFirebase } from "../../wrappers/firebase";
import {
  documentId,
  orderBy,
  serverTimestamp,
  where,
} from "firebase/firestore";
import useUserStore from "../../store/userStore";
import { shallow } from "zustand/shallow";
import chunkArray from "../../helpers/chunkArray";
import useSearch from "./useSearch";

export default () => {
  const { getData, addDocument, getCollectionGroupData, updateDocument } =
    useFirebase();
  const queryClient = useQueryClient();
  const { getProfile } = useProfile();
  const [isFetching, setIsFetching] = useState(false);
  const { id: stateUserId } = useUserStore((state) => state.user, shallow);
  const { executeSearch } = useSearch();
  const suggestedPostRef = useRef({
    postRefs: [],
    lastFetchedIndex: 0,
  });

  async function getPost({
    username,
    userId,
    postId,
    disableProgressUpdate = false,
  }) {
    !disableProgressUpdate && setIsFetching(true);
    const profile = await getProfile({ uid: userId, username });

    // Check if the post exists in the 'posts' cache by id
    const _cachedPost = queryClient.getQueryData(["posts", postId]);
    if (_cachedPost) {
      setIsFetching(false);
      return _cachedPost;
    }

    const post = await getData({
      path: `profiles/${profile.id}/posts/${postId}`,
    });
    if (post) {
      const [likeCount, commentCount, isLikedByUser, isDislikedByUser] =
        await Promise.all([
          getData({
            path: `profiles/${profile.id}/posts/${postId}/likes`,
            getOnlyLength: true,
            filters: [where("isDeleted", "==", false)],
          }),
          getData({
            path: `profiles/${profile.id}/posts/${postId}/comments`,
            getOnlyLength: true,
            filters: [where("isDeleted", "==", false)],
          }),
          getData({
            path: `profiles/${profile.id}/posts/${postId}/likes`,
            getOnlyLength: true,
            filters: [
              where("isDeleted", "==", false),
              where(documentId(), "==", stateUserId),
            ],
          }),
          getData({
            path: `profiles/${profile.id}/posts/${postId}/dislikes`,
            getOnlyLength: true,
            filters: [
              where("isDeleted", "==", false),
              where(documentId(), "==", stateUserId),
            ],
          }),
        ]);
      post.likeCount = post.isShallow ? post.likeCount : likeCount;
      post.commentCount = commentCount;
      post.isLikedByUser = !!isLikedByUser;
      post.isDislikedByUser = !!isDislikedByUser;
      post.user = profile;

      queryClient.setQueryData(["posts", postId], (oldPost) => {
        return oldPost ? { ...oldPost, ...post } : post;
      });
      !disableProgressUpdate && setIsFetching(false);
      return post;
    } else {
      !disableProgressUpdate && setIsFetching(false);
      throw new Error("Post does not exist");
    }
  }

  /**
   *
   * @param {Object} props
   * @param {[{userId: string, postId: string}]} props.reference An array of userId and postId
   * @param {boolean} props.disableProgressUpdate
   */
  async function getMultiplePosts({
    reference,
    disableProgressUpdate = false,
  }) {
    !disableProgressUpdate && setIsFetching(true);
    const groupedByUserId = reference.reduce((acc, { userId, postId }) => {
      if (!acc[userId]) acc[userId] = [];
      acc[userId].push(postId);
      return acc;
    }, {});

    const batchedPosts = Object.keys(groupedByUserId)?.map((userId) => ({
      userId,
      postIdBatches: chunkArray(groupedByUserId[userId], 10),
    }));

    const posts = (
      await Promise.all(
        batchedPosts.map((batch) =>
          Promise.all(
            batch.postIdBatches.map((postIdBatch) =>
              Promise.all(
                postIdBatch.map(
                  async (postId) =>
                    await getPost({
                      userId: batch.userId,
                      postId,
                      disableProgressUpdate: true,
                    })
                )
              )
            )
          )
        )
      )
    )?.flat(Infinity);
    !disableProgressUpdate && setIsFetching(false);
    return posts;
  }

  async function getPostsByUser({ username }) {
    setIsFetching(true);
    const { id: userId } = await getProfile({ username });

    const lastPost = queryClient.getQueryData([
      "profiles",
      username,
      "lastPostRef",
    ]);
    const { docs: posts, last: lastPostRef } = await getData({
      path: `profiles/${userId}/posts`,
      limit: 10,
      filters: [orderBy("createdAt", "desc"), where("isDeleted", "==", false)],
      startAfterDoc: lastPost,
    });

    // Build enriched posts without mutating input and wait for all async work
    const enrichedPosts = [];
    if (posts?.length) {
      await Promise.all(
        posts.map(async (rawPost) => {
          const postId = rawPost.id;

          const [likeCount, commentCount, isLikedByUser, isDislikedByUser] =
            await Promise.all([
              getData({
                path: `profiles/${userId}/posts/${postId}/likes`,
                getOnlyLength: true,
                filters: [where("isDeleted", "==", false)],
              }),
              getData({
                path: `profiles/${userId}/posts/${postId}/comments`,
                getOnlyLength: true,
                filters: [where("isDeleted", "==", false)],
              }),
              getData({
                path: `profiles/${userId}/posts/${postId}/likes`,
                getOnlyLength: true,
                filters: [
                  where("isDeleted", "==", false),
                  where(documentId(), "==", stateUserId),
                ],
              }),
              getData({
                path: `profiles/${userId}/posts/${postId}/dislikes`,
                getOnlyLength: true,
                filters: [
                  where("isDeleted", "==", false),
                  where(documentId(), "==", stateUserId),
                ],
              }),
            ]);

          const userProfile = queryClient.getQueryData(["profiles", username]);
          const post = {
            ...rawPost,
            likeCount: rawPost?.isShallow ? rawPost.likeCount : likeCount,
            commentCount,
            isLikedByUser: !!isLikedByUser,
            isDislikedByUser: !!isDislikedByUser,
            user: userProfile,
          };

          queryClient.setQueryData(["posts", postId], (oldPost) => {
            return oldPost ? { ...oldPost, ...post } : post;
          });

          enrichedPosts.push(post);
        })
      );
    }

    queryClient.setQueryData(
      ["profiles", username, "lastPostRef"],
      () => lastPostRef
    );

    setIsFetching(false);

    if (enrichedPosts.length > 0) return enrichedPosts;
    else {
      const _postsQuery = queryClient.getQueriesData({
        queryKey: ["posts"],
      });
      const allPosts = _postsQuery
        ?.map(([, data]) => data)
        .flat()
        .filter(
          (item) => item && typeof item === "object" && !Array.isArray(item)
        );

      return [...(allPosts || [])].filter(
        (user) => user?.username === username
      );
    }
  }

  async function getSuggestedPosts() {
    // return;
    setIsFetching(true);

    if (suggestedPostRef.current.postRefs.length >= 100) return; // Do not load any more than 100 posts

    const remainingPostsToFetch =
      suggestedPostRef.current.postRefs.length -
      suggestedPostRef.current.lastFetchedIndex -
      1;

    if (remainingPostsToFetch > 0) {
      const postsToFetch = suggestedPostRef.current.postRefs
        .slice(lastFetchedIndex, 20)
        .map(({ objectID, userId }) => ({ postId: objectID, userId }));

      const posts = await getMultiplePosts({
        reference: postsToFetch,
      });
      suggestedPostRef.current.lastFetchedIndex =
        suggestedPostRef.current.lastFetchedIndex + 20;

      setIsFetching(false);

      return posts;
    } else {
      const searchedPosts = await executeSearch({
        type: "posts",
        minQueryLength: 0,
        query: "",
        limit: 100,
      });
      if (searchedPosts?.length > 0) {
        const postsToFetch = searchedPosts
          .slice(0, 20)
          .map(({ objectID, userId }) => ({ postId: objectID, userId }));

        const posts = await getMultiplePosts({
          reference: postsToFetch,
        });

        suggestedPostRef.current = {
          postRefs: searchedPosts,
          lastFetchedIndex: 20,
        };
        setIsFetching(false);
        const posts_w_queryMap = [];

        for (let index = 0; index < posts.length; index++) {
          const _post = posts[index];
          const { queryId } =
            searchedPosts?.find(({ objectID }) => objectID === _post.id) || {};
          if (queryId) {
            posts_w_queryMap.push({ ..._post, queryId });
          }
        }
        return posts_w_queryMap;
      }
    }
  }

  async function getSimilarPosts(postId) {
    const searchResults = await executeSearch({
      type: "posts",
      query: "",
      minQueryLength: 0,
      limit: 10,
      // excludeIDs: [postId],
    });

    const similarPosts = await getMultiplePosts({
      disableProgressUpdate: true,
      reference: searchResults?.map(({ userId, objectID }) => ({
        userId,
        postId: objectID,
      })),
    });

    return similarPosts;
  }

  function deletePost({ postId }) {
    queryClient.removeQueries({ queryKey: ["posts", postId] });

    (async () => {
      await updateDocument({
        path: `profiles/${stateUserId}/posts/${postId}`,
        data: { isDeleted: true, deletedOn: serverTimestamp() },
      });
    })();
  }

  return {
    getPost,
    getPostsByUser,
    getMultiplePosts,
    getSimilarPosts,
    getSuggestedPosts,
    deletePost,
    isFetching,
  };
};
